/**
 * A small localStorage record belonging to one hire, forgotten after a while.
 *
 * The walkthrough has to remember two things across the relaunch macOS asks for -- an attempt in
 * progress and a refusal -- so the mechanism is named once here. Both are keyed by person because
 * laptops are handed on, and both expire because a record kept forever outlives the session it
 * describes and would decide a later hire's onboarding.
 */

interface Stored<T> {
  readonly personId: string;
  readonly writtenAtMs: number;
  readonly payload: T;
}

/** Reading, writing and forgetting one such record. */
export interface PersonRecord<T> {
  /** No-ops before sign-in resolves a person, since the record could never be read back. */
  readonly write: (personId: string | undefined, payload: T) => void;
  /** The payload if one is stored for this hire and still fresh, otherwise undefined. */
  readonly read: (personId: string | undefined) => T | undefined;
  readonly clear: () => void;
}

/**
 * Build a record store.
 *
 * Every storage failure is swallowed: these records only ever soften an optional offer or a funnel
 * number, so losing one degrades behaviour rather than breaking the screen the hire is using.
 *
 * @param storageKey The localStorage key to keep it under.
 * @param ttlMs How long the record is still believed.
 * @param isPayload Narrows what was parsed, because the stored shape is only as good as the last build that wrote it.
 */
export const personRecord = <T>(
  storageKey: string,
  ttlMs: number,
  isPayload: (value: unknown) => value is T
): PersonRecord<T> => ({
  write: (personId, payload) => {
    if (personId === undefined) {
      return;
    }
    try {
      const record: Stored<T> = { payload, personId, writtenAtMs: Date.now() };
      localStorage.setItem(storageKey, JSON.stringify(record));
    } catch {
      // Deliberately swallowed; see above.
    }
  },

  read: (personId) => {
    if (personId === undefined) {
      return undefined;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null) {
        return undefined;
      }
      const record = JSON.parse(raw) as Partial<Stored<T>>;
      if (
        record.personId !== personId ||
        typeof record.writtenAtMs !== "number" ||
        Date.now() - record.writtenAtMs > ttlMs ||
        !isPayload(record.payload)
      ) {
        return undefined;
      }
      return record.payload;
    } catch {
      /* A record we cannot read is one we cannot attribute, so it is treated as absent. */
      return undefined;
    }
  },

  clear: () => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Deliberately swallowed; see above.
    }
  },
});
